import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ScanLine, LineChart, ShieldCheck, Package,
  Check, Monitor, Download, ArrowRight, Zap
} from 'lucide-react';

const DOWNLOAD_URL = 'https://github.com/kabilberke0/cularix_frontend/releases/download/v0.0.2/Cularix.POS.Setup.0.0.2.exe';

const features = [
  {
    icon: ScanLine,
    title: 'Akıllı QR Menü',
    desc: 'Müşterileriniz masalarındaki QR kodu okutarak anında güncel menüye ulaşsın ve beklemeden sipariş versin.',
    color: '#3b82f6',
    glow: 'rgba(59,130,246,0.15)',
  },
  {
    icon: LineChart,
    title: 'Detaylı Ciro Takibi',
    desc: 'Hangi ürün daha çok satıyor? Günlük ve aylık gelirleriniz ne alemde? Her şey saniyesi saniyesine raporlarda.',
    color: '#22c55e',
    glow: 'rgba(34,197,94,0.15)',
  },
  {
    icon: ShieldCheck,
    title: 'Banka Düzeyinde Güvenlik',
    desc: 'Sistemimiz gelişmiş siber güvenlik kalkanları ve şifreleme algoritmalarıyla korunmaktadır. Verileriniz tamamen güvende.',
    color: '#a855f7',
    glow: 'rgba(168,85,247,0.15)',
  },
  {
    icon: Package,
    title: 'Stok Yönetimi',
    desc: 'Hammadde reçeteleri tanımlayın, sipariş anında stok otomatik düşsün. Hiç stok bitmesi yaşamayın.',
    color: '#f97316',
    glow: 'rgba(249,115,22,0.15)',
  },
];

const plans = [
  {
    label: '1 Aylık',
    price: '300',
    unit: 'TL / ay',
    monthly: null,
    badge: null,
    features: ['Sınırsız QR Menü', 'Sınırsız Sipariş', 'Teknik Destek'],
    featured: false,
    cta: 'Satın Al',
  },
  {
    label: '3 Aylık',
    price: '850',
    unit: 'TL',
    monthly: '~283 TL / ay',
    badge: null,
    features: ['Tüm Temel Özellikler', 'Personel Yönetimi'],
    featured: false,
    cta: 'Satın Al',
  },
  {
    label: '6 Aylık',
    price: '1.600',
    unit: 'TL',
    monthly: '~266 TL / ay',
    badge: '⭐ En Çok Tercih Edilen',
    features: ['Tüm Temel Özellikler', 'Detaylı Ciro Raporları', 'Öncelikli Destek'],
    featured: true,
    cta: 'Hemen Başla',
  },
  {
    label: 'Yıllık',
    price: '3.100',
    unit: 'TL',
    monthly: '~258 TL / ay',
    badge: null,
    features: ['Tüm Premium Özellikler', 'Ücretsiz Güncellemeler'],
    featured: false,
    cta: 'Satın Al',
  },
];

const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: '#07070B', color: '#fff' }}>

      {/* ── AMBIENT BACKGROUND ── */}
      <div className="fixed inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 55% at 50% -10%, rgba(249,115,22,0.13) 0%, transparent 70%)',
      }} />

      {/* ── NAVBAR ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: scrolled ? 'rgba(7,7,11,0.82)' : 'transparent',
          backdropFilter: scrolled ? 'blur(18px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.07)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <img src="/logo.png" alt="Cularix" className="w-8 h-8 object-contain" />
              <span className="font-bold text-lg text-white tracking-tight">Cularix</span>
            </div>

            {/* Links */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollTo('ozellikler')} className="text-sm text-white/50 hover:text-white transition-colors">Özellikler</button>
              <button onClick={() => scrollTo('fiyatlar')} className="text-sm text-white/50 hover:text-white transition-colors">Fiyatlar</button>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Link to="/login"
                className="hidden sm:block text-sm text-white/60 hover:text-white font-medium px-4 py-2 rounded-lg hover:bg-white/5 transition-all">
                Kayıt Ol
              </Link>
              <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm font-semibold bg-orange-500 hover:bg-orange-400 text-white px-4 py-2 rounded-lg transition-all shadow-lg shadow-orange-500/25">
                <Monitor size={14} />
                <span className="hidden sm:inline">Windows v0.0.2</span>
                <span className="sm:hidden">İndir</span>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center justify-center px-4 pt-16">
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.025]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }} />

        {/* Glow orb */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.07) 0%, transparent 70%)' }} />

        <div className="relative z-10 text-center max-w-5xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 border rounded-full px-4 py-1.5 mb-8 text-sm"
            style={{ borderColor: 'rgba(249,115,22,0.3)', backgroundColor: 'rgba(249,115,22,0.08)', color: '#fb923c' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
            Yeni Nesil Restoran Yönetim Sistemi
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight leading-tight mb-6">
            İşletmenizi{' '}
            <span className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(135deg, #f97316 0%, #fb923c 40%, #fbbf24 100%)' }}>
              Geleceğe
            </span>
            {' '}Taşıyın
          </h1>

          <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10" style={{ color: 'rgba(255,255,255,0.45)' }}>
            QR menü, canlı sipariş takibi, personel yönetimi ve detaylı ciro raporlarıyla restoranınızı cebinizden yönetin.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login"
              className="w-full sm:w-auto flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-2xl text-base transition-all"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)' }}>
              Ücretsiz Kayıt Ol
              <ArrowRight size={17} />
            </Link>
            <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer"
              className="w-full sm:w-auto flex items-center justify-center gap-3 font-semibold px-8 py-4 rounded-2xl text-base transition-all bg-orange-500 hover:bg-orange-400 text-white"
              style={{ boxShadow: '0 8px 32px rgba(249,115,22,0.35)' }}>
              <Monitor size={20} />
              <div className="text-left">
                <div className="leading-tight">Windows İçin İndir</div>
                <div className="text-xs opacity-70 font-normal">Sürüm 0.0.2 • Güvenli Kurulum</div>
              </div>
              <Download size={17} className="opacity-60" />
            </a>
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-center gap-8 mt-16 flex-wrap">
            {[
              { value: 'Sınırsız', label: 'QR Sipariş' },
              { value: '7/24', label: 'Teknik Destek' },
              { value: '1 dk', label: 'Kurulum' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-bold text-white">{s.value}</div>
                <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="ozellikler" className="py-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#f97316' }}>Özellikler</p>
            <h2 className="text-4xl font-bold text-white mb-4">İhtiyacınız Olan Her Şey Tek Ekranda</h2>
            <p className="max-w-xl mx-auto text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Kullanımı kolay, hızlı ve güvenli altyapımızla işletmenizin verimliliğini artırın.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <div key={i} className="relative group rounded-3xl p-6 transition-all duration-300 overflow-hidden"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}>
                {/* Hover glow */}
                <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `radial-gradient(circle at 30% 20%, ${f.glow}, transparent 70%)` }} />

                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${f.color}18`, border: `1px solid ${f.color}30` }}>
                    <f.icon size={22} style={{ color: f.color }} />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2">{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="fiyatlar" className="py-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#f97316' }}>Fiyatlandırma</p>
            <h2 className="text-4xl font-bold text-white mb-4">Esnek Fiyatlandırma Planları</h2>
            <p className="max-w-xl mx-auto text-base" style={{ color: 'rgba(255,255,255,0.4)' }}>
              İşletmenizin ölçeğine ve bütçenize en uygun paketi seçin.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
            {plans.map((plan, i) => (
              <div key={i} className={`relative rounded-3xl p-6 flex flex-col transition-all duration-300 ${plan.featured ? 'lg:-mt-5' : ''}`}
                style={{
                  backgroundColor: plan.featured ? 'rgba(249,115,22,0.08)' : 'rgba(255,255,255,0.025)',
                  border: plan.featured ? '1.5px solid rgba(249,115,22,0.45)' : '1px solid rgba(255,255,255,0.07)',
                  boxShadow: plan.featured ? '0 0 60px rgba(249,115,22,0.12)' : 'none',
                }}>
                {plan.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span className="text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg"
                      style={{ backgroundColor: '#f97316', boxShadow: '0 4px 16px rgba(249,115,22,0.4)' }}>
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className={plan.badge ? 'pt-3' : ''}>
                  <h3 className="font-semibold text-sm mb-4" style={{ color: plan.featured ? '#fb923c' : 'rgba(255,255,255,0.5)' }}>
                    {plan.label}
                  </h3>
                  <div className="mb-1">
                    <span className="text-4xl font-extrabold text-white">{plan.price}</span>
                    <span className="text-sm ml-1.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{plan.unit}</span>
                  </div>
                  {plan.monthly
                    ? <p className="text-xs font-medium mb-5" style={{ color: '#4ade80' }}>{plan.monthly}</p>
                    : <div className="mb-5" />
                  }

                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feat, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
                        <Check size={14} style={{ color: plan.featured ? '#f97316' : '#22c55e', flexShrink: 0 }} />
                        {feat}
                      </li>
                    ))}
                  </ul>

                  <Link to="/login"
                    className="w-full block text-center py-3 rounded-xl font-semibold text-sm transition-all"
                    style={plan.featured
                      ? { backgroundColor: '#f97316', color: '#fff', boxShadow: '0 4px 20px rgba(249,115,22,0.35)' }
                      : { backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.1)' }
                    }>
                    {plan.cta}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }} className="py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Cularix" className="w-6 h-6 object-contain" />
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
              © {new Date().getFullYear()} Cularix Yazılım. Tüm hakları saklıdır. (v0.0.2)
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/legal/privacy" className="text-xs transition-colors" style={{ color: 'rgba(255,255,255,0.25)' }}>Gizlilik Politikası</Link>
            <Link to="/legal/terms" className="text-xs transition-colors" style={{ color: 'rgba(255,255,255,0.25)' }}>Kullanım Koşulları</Link>
            <Link to="/legal/kvkk" className="text-xs transition-colors" style={{ color: 'rgba(255,255,255,0.25)' }}>KVKK</Link>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
